(() => {
  /**
   * Push an explicit Termly decision into Shopify Customer Privacy so
   * Shopify-managed pixels (Meta fbevents.js, etc.) follow Decline / Accept.
   *
   * First visit with no Termly decision: do not call setTrackingConsent.
   * Termly.getConsentState() in opt_out regions returns category=true before
   * any click (defaults, not consent). Inventing a grant would load pixels;
   * inventing a decline would record consent without a visitor interaction
   * (Shopify forbids that). Decline must still win the moment it is explicit.
   */
  const TERMLY_WAIT_MS = 10000;
  const TERMLY_CACHE_KEY = 'TERMLY_API_CACHE';
  const TERMLY_CONSENT_KEY = 'TERMLY_COOKIE_CONSENT';

  let termlyConsentMode = null;
  let termlyEventDecision = null;
  let lastWrite = null;
  let lastError = null;
  let lastSkipReason = 'pending';
  let writeCount = 0;
  const MAX_WRITES = 10;
  let apiReady = false;

  const isGranted = (value) => value === true || value === 'true' || value === 'yes' || value === 1;

  const termlyExpected = () =>
    Boolean(window.Termly || document.querySelector('script[src*="app.termly.io"]'));

  const termlyStoredConsents = () => {
    try {
      const raw = window.localStorage.getItem(TERMLY_CACHE_KEY);
      if (!raw) return null;
      const entry = JSON.parse(raw)[TERMLY_CONSENT_KEY];
      if (!entry || entry.value == null || typeof entry.value !== 'object') return null;
      return entry.value;
    } catch (error) {
      return null;
    }
  };

  const termlyHasExplicitDecision = () => {
    if (termlyEventDecision && typeof termlyEventDecision === 'object') return true;
    return termlyStoredConsents() != null;
  };

  const categoriesToState = (categories) => {
    if (!Array.isArray(categories)) return null;
    const set = new Set(categories.map((name) => String(name)));
    return {
      essential: set.has('essential'),
      analytics: set.has('analytics'),
      advertising: set.has('advertising'),
      performance: set.has('performance'),
      social_networking: set.has('social_networking'),
      unclassified: set.has('unclassified'),
    };
  };

  const rememberTermlyDecision = (data) => {
    if (!data || typeof data !== 'object') return;
    if (data.consentState && typeof data.consentState === 'object') {
      termlyEventDecision = data.consentState;
      return;
    }
    const fromCategories = categoriesToState(data.categories);
    if (fromCategories) termlyEventDecision = fromCategories;
  };

  const readTermlyCategories = () => {
    if (termlyEventDecision && typeof termlyEventDecision === 'object') return termlyEventDecision;
    const stored = termlyStoredConsents();
    if (stored) return stored;
    if (window.Termly && typeof window.Termly.getConsentState === 'function') {
      return window.Termly.getConsentState() || {};
    }
    return null;
  };

  const mapToShopify = (termly) => {
    const analytics = isGranted(termly.analytics);
    const marketing = isGranted(termly.advertising);
    const preferences = isGranted(termly.preferences) || isGranted(termly.personalization) || isGranted(termly.performance);
    // Termly Decline All leaves do_not_sell=false; advertising already denies sale_of_data.
    // A separate DNS / GPC opt-out sets do_not_sell=true and must deny sale_of_data even after Accept.
    const saleOfData = marketing && !isGranted(termly.do_not_sell);
    return {
      analytics,
      marketing,
      preferences,
      sale_of_data: saleOfData,
    };
  };

  const shopifyPrivacy = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.setTrackingConsent !== 'function') return null;
    return privacy;
  };

  const shopifySnapshot = () => {
    const privacy = shopifyPrivacy();
    if (!privacy) return { ready: false };
    const consent =
      typeof privacy.currentVisitorConsent === 'function' ? privacy.currentVisitorConsent() : null;
    return {
      ready: true,
      currentVisitorConsent: consent,
      analyticsProcessingAllowed:
        typeof privacy.analyticsProcessingAllowed === 'function' ? privacy.analyticsProcessingAllowed() : null,
      marketingAllowed: typeof privacy.marketingAllowed === 'function' ? privacy.marketingAllowed() : null,
      preferencesProcessingAllowed:
        typeof privacy.preferencesProcessingAllowed === 'function' ? privacy.preferencesProcessingAllowed() : null,
      saleOfDataAllowed: typeof privacy.saleOfDataAllowed === 'function' ? privacy.saleOfDataAllowed() : null,
    };
  };

  const consentMatches = (current, next) => {
    if (!current) return false;
    const asToken = (value) => (value === true ? 'yes' : value === false ? 'no' : String(value || ''));
    return (
      asToken(current.analytics) === asToken(next.analytics ? 'yes' : 'no') &&
      asToken(current.marketing) === asToken(next.marketing ? 'yes' : 'no') &&
      asToken(current.preferences) === asToken(next.preferences ? 'yes' : 'no') &&
      asToken(current.sale_of_data) === asToken(next.sale_of_data ? 'yes' : 'no')
    );
  };

  const writeShopifyConsent = (payload, reason) => {
    const privacy = shopifyPrivacy();
    if (!privacy) {
      lastSkipReason = 'shopify-api-not-ready';
      return;
    }

    const current = typeof privacy.currentVisitorConsent === 'function' ? privacy.currentVisitorConsent() : null;
    if (consentMatches(current, payload)) {
      lastSkipReason = 'already-aligned';
      lastWrite = { payload, reason, skipped: true };
      return;
    }

    if (writeCount >= MAX_WRITES) {
      lastSkipReason = 'write-cap';
      return;
    }

    lastSkipReason = 'writing';
    writeCount += 1;
    privacy.setTrackingConsent(payload, (result) => {
      if (result && result.error) {
        lastError = result.error;
        lastSkipReason = 'write-error';
        lastWrite = { payload, reason, error: result.error };
        return;
      }
      lastError = null;
      lastSkipReason = 'synced';
      lastWrite = { payload, reason, skipped: false };
    });
  };

  const syncShopify = (reason) => {
    if (!apiReady) {
      lastSkipReason = 'shopify-api-not-ready';
      return;
    }

    if (!termlyHasExplicitDecision()) {
      lastSkipReason = 'no-explicit-termly-decision';
      return;
    }

    const termly = readTermlyCategories();
    if (!termly) {
      lastSkipReason = 'termly-state-unavailable';
      return;
    }

    writeShopifyConsent(mapToShopify(termly), reason);
  };

  const listenTermly = () => {
    if (!window.Termly || typeof window.Termly.on !== 'function') return;
    window.Termly.on('initialized', (data) => {
      if (data && data.region_settings && data.region_settings.consent_mode) {
        termlyConsentMode = data.region_settings.consent_mode;
      }
      syncShopify('termly-initialized');
    });
    window.Termly.on('consent', (data) => {
      rememberTermlyDecision(data);
      // Official Termly payloads sometimes omit consentState. The event itself
      // is the visitor decision — fall back to getConsentState() after the click.
      if (!termlyEventDecision && window.Termly && typeof window.Termly.getConsentState === 'function') {
        termlyEventDecision = window.Termly.getConsentState() || null;
      }
      syncShopify('termly-consent');
    });
  };

  const whenTermlyReady = (callback) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      callback();
    };

    const attach = () => {
      listenTermly();
      finish();
    };

    if (window.Termly && typeof window.Termly.getConsentState === 'function') {
      attach();
      return;
    }

    if (!termlyExpected()) {
      lastSkipReason = 'termly-not-present';
      finish();
      return;
    }

    const started = Date.now();
    const timer = setInterval(() => {
      if (window.Termly && typeof window.Termly.getConsentState === 'function') {
        clearInterval(timer);
        attach();
      } else if (Date.now() - started > TERMLY_WAIT_MS) {
        clearInterval(timer);
        finish();
      }
    }, 50);
  };

  const loadShopifyPrivacy = (callback) => {
    if (shopifyPrivacy()) {
      apiReady = true;
      callback();
      return;
    }

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      lastSkipReason = 'shopify-loadfeatures-missing';
      callback();
      return;
    }

    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) {
        lastError = error;
        lastSkipReason = 'shopify-loadfeatures-error';
        callback();
        return;
      }
      apiReady = Boolean(shopifyPrivacy());
      callback();
    });
  };

  const termlyDebugState = () => {
    const live =
      window.Termly && typeof window.Termly.getConsentState === 'function'
        ? window.Termly.getConsentState()
        : null;
    const stored = termlyStoredConsents();
    const explicit = termlyHasExplicitDecision();
    const source = explicit ? readTermlyCategories() : live;
    return {
      ready: Boolean(live),
      explicit,
      consentMode: termlyConsentMode,
      analytics: source ? isGranted(source.analytics) : false,
      advertising: source ? isGranted(source.advertising) : false,
      performance: source ? isGranted(source.performance) : false,
      essential: source ? isGranted(source.essential) : true,
      social_networking: source ? isGranted(source.social_networking) : false,
      do_not_sell: source ? isGranted(source.do_not_sell) : false,
      live,
      stored,
      eventDecision: termlyEventDecision,
    };
  };

  const alignment = (termly, shopify) => {
    const consent = (shopify && shopify.currentVisitorConsent) || {};
    const token = (value) => (isGranted(value) || value === 'yes' ? 'yes' : value === '' || value == null ? '' : 'no');
    const mapped = termly.explicit
      ? mapToShopify({
          analytics: termly.analytics,
          advertising: termly.advertising,
          performance: termly.performance,
          preferences: termly.live && termly.live.preferences,
          personalization: termly.live && termly.live.personalization,
          do_not_sell: termly.do_not_sell,
        })
      : null;
    const expected = mapped
      ? {
          analytics: mapped.analytics ? 'yes' : 'no',
          marketing: mapped.marketing ? 'yes' : 'no',
          preferences: mapped.preferences ? 'yes' : 'no',
          sale_of_data: mapped.sale_of_data ? 'yes' : 'no',
        }
      : null;
    return {
      synced: Boolean(expected) &&
        token(consent.analytics) === expected.analytics &&
        token(consent.marketing) === expected.marketing &&
        token(consent.preferences) === expected.preferences &&
        token(consent.sale_of_data) === expected.sale_of_data,
      expected,
      shopifyVisitor: {
        analytics: token(consent.analytics),
        marketing: token(consent.marketing),
        preferences: token(consent.preferences),
        sale_of_data: token(consent.sale_of_data),
      },
    };
  };

  window.__tileclubShopifyConsent = {
    sync: () => syncShopify('manual'),
    debug: () => {
      const termly = termlyDebugState();
      const shopify = shopifySnapshot();
      return {
        termly,
        shopify,
        alignment: alignment(termly, shopify),
        mapping: {
          'termly.analytics': 'shopify.analytics',
          'termly.advertising': 'shopify.marketing; sale_of_data unless termly.do_not_sell',
          'termly.performance': 'shopify.preferences',
          'termly.essential': 'not written (checkout / strictly necessary stay allowed)',
        },
        lastWrite,
        lastSkipReason,
        lastError,
        writeCount,
        policy: 'sync-only-after-explicit-termly-decision',
      };
    },
  };

  // Keep the HubSpot helper William already uses in sync with visitor consent.
  const attachHubspotDebugAlias = () => {
    const hubspot = window.__tileclubHubspotConsent;
    if (!hubspot || typeof hubspot.debug !== 'function' || hubspot.__shopifyBridgeWrapped) return;
    const original = hubspot.debug;
    hubspot.__shopifyBridgeWrapped = true;
    hubspot.debug = () => {
      const result = original();
      result.shopifyConsentSync = window.__tileclubShopifyConsent.debug();
      return result;
    };
  };

  loadShopifyPrivacy(() => {
    document.addEventListener('visitorConsentCollected', () => {
      // Re-apply an explicit Termly Decline if Shopify's own banner writes yes.
      syncShopify('visitor-consent-collected');
    });
    whenTermlyReady(() => {
      attachHubspotDebugAlias();
      syncShopify('ready');
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachHubspotDebugAlias);
  } else {
    attachHubspotDebugAlias();
  }
})();
